import { useState } from 'react';
import type { Hotel } from '../types/hotel';
import { 
  Building2, 
  MapPin, 
  Star, 
  Wifi, 
  Car, 
  Coffee, 
  Dumbbell, 
  Waves,
  ChevronRight,
  Plus,
  ExternalLink,
  Calendar,
  Users
} from 'lucide-react';
import { formatHotelPrice } from '../types/hotel';

interface HotelCardProps {
  hotel: Hotel;
  numberOfNights?: number;
  onSelect?: (hotel: Hotel) => void;
  onAddToItinerary?: (hotel: Hotel) => void;
  isSelected?: boolean;
  className?: string;
}

// Get amenity icon
function getAmenityIcon(amenity: string) {
  const amenityIcons: Record<string, typeof Wifi> = {
    'Wi-Fi': Wifi,
    'Parking': Car,
    'Breakfast': Coffee,
    'Gym': Dumbbell,
    'Pool': Waves,
  };
  
  const Icon = amenityIcons[amenity] || Wifi;
  return <Icon className="h-4 w-4" />;
}

// Format star rating
function formatStarRating(rating: number): string {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export default function HotelCard({
  hotel,
  numberOfNights = 1,
  onSelect,
  onAddToItinerary,
  isSelected = false,
  className = '',
}: HotelCardProps) {
  const [imageIndex, setImageIndex] = useState(0);
  const {
    name,
    location,
    rating,
    starRating,
    reviews,
    images = [],
    price,
    amenities = [],
    contact,
  } = hotel;

  const totalPrice = price.perNight * numberOfNights;
  const primaryImage = images[0]?.url || hotel.primaryImage;

  return (
    <div 
      className={`p-4 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-purple-50 border-2 border-purple-500' : 'border-b border-gray-100'
      } ${className}`}
    >
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Image Gallery */}
        <div className="lg:w-64 lg:flex-shrink-0">
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100">
            {primaryImage ? (
              <img
                src={primaryImage}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-12 w-12 text-gray-300" />
              </div>
            )}
            
            {/* Image Navigation Dots */}
            {images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {images.slice(0, 5).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === imageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Quick View Button */}
            <button
              className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              onClick={() => onSelect?.(hotel)}
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Hotel Info */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">{name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-yellow-500">{formatStarRating(starRating)}</span>
                <span className="text-sm text-gray-500">{starRating}-star hotel</span>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
            <MapPin className="h-4 w-4" />
            <span>{location.area || location.city}, {location.country}</span>
            {location.distanceFromCenter && (
              <span className="text-gray-400">
                ({location.distanceFromCenter} km from center)
              </span>
            )}
          </div>

          {/* Rating */}
          <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-lg">
              <span className="font-bold">{rating.overall.toFixed(1)}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {rating.overall >= 4.5 ? 'Excellent' : 
                 rating.overall >= 4.0 ? 'Very Good' :
                 rating.overall >= 3.5 ? 'Good' : 'Fair'}
              </p>
              <p className="text-xs text-gray-500">
                {reviews.totalCount} reviews
              </p>
            </div>
          </div>

          {/* Amenities */}
          <div className="flex flex-wrap gap-2 mt-3">
            {amenities.slice(0, 5).map((amenity, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600"
                title={amenity.name}
              >
                {getAmenityIcon(amenity.name)}
                {amenity.name}
              </span>
            ))}
            {amenities.length > 5 && (
              <span className="text-xs text-gray-500">
                +{amenities.length - 5} more
              </span>
            )}
          </div>
        </div>

        {/* Price and Actions */}
        <div className="lg:w-48 lg:flex-shrink-0 flex flex-row lg:flex-col justify-between lg:justify-start gap-4">
          <div className="text-right lg:text-left">
            <p className="text-2xl font-bold text-gray-900">
              {formatHotelPrice(price.perNight, price.currency)}
            </p>
            <p className="text-xs text-gray-500">per night</p>
            {numberOfNights > 1 && (
              <p className="text-sm text-gray-600 mt-1">
                {formatHotelPrice(totalPrice, price.currency)} total
              </p>
            )}
          </div>

          <div className="flex flex-row lg:flex-col gap-2 lg:gap-2">
            {onAddToItinerary && (
              <button
                onClick={() => onAddToItinerary(hotel)}
                className="flex-1 lg:flex-none px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            )}
            {onSelect && (
              <button
                onClick={() => onSelect(hotel)}
                className={`flex-1 lg:flex-none px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isSelected
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Select
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          {contact?.phone && (
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Free cancellation
            </span>
          )}
          {contact?.phone && (
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {price.pricePerNightAdults ? '2 guests' : '1 room'}
            </span>
          )}
        </div>
        
        <button
          onClick={() => onSelect?.(hotel)}
          className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          View details
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
